import { Button } from '../../components/Button';
import { ContentParser } from '../../components/ContentParser';
import { NotificationBox } from '../../components/NotificationBox';
import { PageHeading } from '../../components/PageHeading';
import { PanelCard } from '../../components/PanelCard';
import { SideImage } from '../../components/SideImage';
import { SkeletonCard } from '../../components/SkeletonCard';
import { StickyFooter } from '../../components/StickyFooter';
import { GuestDetailsPanel } from '../../composites/GuestDetailsPanel';
import { SplitLayout } from '../../composites/SplitLayout';
import { HideOnMobile, NotificationContainer, ScrollContainer } from './SpaClassBookingForm.styles';
import type { SpaClassBookingFormProps } from './SpaClassBookingForm.types';

export function SpaClassBookingForm({
  imageUrl,
  imageAlt,
  onBack,
  isLoading,
  telephoneOnly,
  unavailable,
  guests,
  onExistingGuestSelect,
  onGuestChange,
  titleOptions,
  genderOptions,
  stickyFooter,
  labels,
}: SpaClassBookingFormProps) {
  const { title, subtitle, stickyFooter: stickyFooterLabels } = labels;

  const showTelephoneOnly = telephoneOnly && !!labels.telephoneOnly;
  const showUnavailable = unavailable && !!labels.unavailable;

  const selectedGuestIds = guests
    .map((g) => g.selectedGuestId)
    .filter((id): id is string => id !== undefined);

  const right = showTelephoneOnly || showUnavailable ? (
    <NotificationContainer>
      <PageHeading title={title} subtitle={subtitle} />
      {showTelephoneOnly && (
        <NotificationBox
          title={labels.telephoneOnly!.title}
          subtitle={<ContentParser text={labels.telephoneOnly!.subtitle} />}
        />
      )}
      {showUnavailable && (
        <NotificationBox
          title={labels.unavailable!.title}
          subtitle={<ContentParser text={labels.unavailable!.subtitle} />}
          cta={
            <Button
              variant="primary"
              onClick={labels.unavailable!.onCtaClick ?? onBack}
            >
              {labels.unavailable!.ctaLabel}
            </Button>
          }
        />
      )}
    </NotificationContainer>
  ) : (
    <>
      <ScrollContainer>
        <PageHeading title={title} subtitle={subtitle} />

        {isLoading
          ? guests.map((g) => <SkeletonCard key={g.guestNumber} variant="simple" />)
          : guests.map((guest, guestIndex) => {
              const disabledGuestIds = selectedGuestIds.filter(
                (id) => id !== guest.selectedGuestId
              );

              return (
                <PanelCard key={guest.guestNumber}>
                  <GuestDetailsPanel
                    guestNumber={guest.guestNumber}
                    existingGuests={guest.existingGuests}
                    selectedGuestId={guest.selectedGuestId}
                    disabledGuestIds={disabledGuestIds}
                    onExistingGuestSelect={(guestId) => onExistingGuestSelect(guestIndex, guestId)}
                    values={guest.values}
                    onChange={(field, value) => onGuestChange(guestIndex, field, value)}
                    titleOptions={titleOptions}
                    genderOptions={genderOptions}
                    errors={guest.errors}
                    labels={labels.guestDetails}
                  />
                </PanelCard>
              );
            })}
      </ScrollContainer>
      {stickyFooter && stickyFooterLabels && (
        <StickyFooter
          {...stickyFooter}
          totalLabel={stickyFooterLabels.totalLabel}
          ctaLabel={stickyFooterLabels.ctaLabel}
        />
      )}
    </>
  );

  return (
    <SplitLayout
      left={
        <HideOnMobile>
          <SideImage src={imageUrl} alt={imageAlt} sticky onBack={onBack} />
        </HideOnMobile>
      }
      right={right}
      contentGap="0px"
      contentMaxWidth="100%"
    />
  );
}
